import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Shield, Award, CheckCircle, FileCheck, Microscope, ThermometerSun, Zap, Timer, Phone, Factory } from 'lucide-react';
import { Button } from '@/components/ui/button';

const QualityCertifications = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const certifications = [
    {
      icon: Award,
      title: 'ISO 9001:2015',
      subtitle: 'Quality Management System',
      desc: 'Our manufacturing facility is ISO 9001:2015 certified, ensuring consistent quality management processes, documented procedures, and continuous improvement in all operations.'
    },
    {
      icon: Shield,
      title: 'BIS Certification',
      subtitle: 'Bureau of Indian Standards',
      desc: 'All RSD mobile chargers are BIS certified (IS 13252), meeting mandatory Indian safety standards for electrical equipment. Required for legal sale of chargers in India.'
    },
    {
      icon: FileCheck,
      title: 'Legal Metrology',
      subtitle: 'Weights & Measures Compliance',
      desc: 'Our products comply with Legal Metrology (Packaged Commodities) Rules, with proper MRP declarations, manufacturer details, and consumer information.'
    },
    {
      icon: Award,
      title: 'CE Marking',
      subtitle: 'European Conformity',
      desc: 'Products for export markets carry CE marking, indicating conformity with EU health, safety, and environmental protection standards.'
    }
  ];

  const qualityProcess = [
    {
      step: '01',
      title: 'Raw Material Inspection',
      desc: 'Every incoming material batch is tested: copper wire purity, plastic quality, connector materials, PCB components. Only approved materials enter production.',
      icon: Microscope
    },
    {
      step: '02',
      title: 'In-Process Quality Checks',
      desc: 'Quality checkpoints at each production stage: wire cutting, connector assembly, soldering, molding. Issues caught and resolved immediately.',
      icon: CheckCircle
    },
    {
      step: '03',
      title: 'Electrical Safety Testing',
      desc: '100% of products tested for: continuity, insulation resistance, voltage withstand, leakage current. Ensures electrical safety compliance.',
      icon: Zap
    },
    {
      step: '04',
      title: 'Fast Charging Verification',
      desc: 'Protocol-specific testing: QC handshake verification, PD negotiation testing, VOOC protocol confirmation. Ensures fast charging actually works.',
      icon: Timer
    },
    {
      step: '05',
      title: 'Durability Testing',
      desc: 'Bend test (10,000+ cycles), connector insertion test (5,000+ cycles), pull force test, drop test. Ensures long product life.',
      icon: Shield
    },
    {
      step: '06',
      title: 'Temperature Testing',
      desc: 'Heat generation during charging monitored. Products must stay within safe temperature limits during maximum power operation.',
      icon: ThermometerSun
    }
  ];

  const qualityStats = [
    { value: '100%', label: 'Products Tested' },
    { value: '<0.5%', label: 'Defect Rate' },
    { value: '10,000+', label: 'Bend Cycles Tested' },
    { value: '8+', label: 'Quality Checkpoints' }
  ];

  return (
    <>
      <Helmet>
        <title>Quality & Certifications | RSD Accessories - ISO 9001:2015 | BIS Certified</title>
        <meta name="description" content="Learn about RSD Accessories quality standards - ISO 9001:2015 certification, BIS certified chargers, rigorous testing process. 100% products tested, &lt;0.5% defect rate." />
        <meta name="keywords" content="rsd accessories quality, iso certified manufacturer, bis certified charger, quality testing mobile accessories, manufacturing quality standards" />
        <link rel="canonical" href="https://rsdaccessories.com/quality-certifications" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "RSD Accessories Pvt Ltd",
            "hasCredential": [
              {
                "@type": "EducationalOccupationalCredential",
                "credentialCategory": "certification",
                "name": "ISO 9001:2015",
                "description": "Quality Management System Certification"
              },
              {
                "@type": "EducationalOccupationalCredential",
                "credentialCategory": "certification",
                "name": "BIS Certification",
                "description": "Bureau of Indian Standards - IS 13252 for Mobile Chargers"
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero */}
        <section className="bg-gradient-to-br from-neutral-900 to-black py-20">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <Shield className="w-20 h-20 text-[#FF4D00] mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
              Quality & Certifications
            </h1>
            <p className="text-xl text-neutral-300 max-w-2xl mx-auto">
              ISO 9001:2015 certified facility | BIS certified products | 100% quality tested
            </p>
          </div>
        </section>

        {/* Quality Stats */}
        <section className="py-8 bg-[#FF4D00]">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-white">
              {qualityStats.map((stat, idx) => (
                <div key={idx}>
                  <div className="text-3xl font-black">{stat.value}</div>
                  <div className="text-sm text-white/80">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Certifications */}
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Certifications</h2>
              <p className="text-neutral-600 max-w-2xl mx-auto">
                International and national certifications that validate our commitment to quality and safety
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {certifications.map((cert, idx) => (
                <div key={idx} className="bg-white rounded-xl p-8 border border-neutral-200 hover:border-[#FF4D00] hover:shadow-lg transition-all">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-[#FF4D00]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <cert.icon className="w-8 h-8 text-[#FF4D00]" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-neutral-900">{cert.title}</h3>
                      <p className="text-[#FF4D00] font-medium mb-2">{cert.subtitle}</p>
                      <p className="text-neutral-600">{cert.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Quality Process */}
        <section className="py-16 bg-neutral-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Quality Process</h2>
              <p className="text-neutral-600 max-w-2xl mx-auto">
                6-stage quality assurance ensures every product meets our high standards
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {qualityProcess.map((process, idx) => (
                <div key={idx} className="bg-white rounded-xl p-6 shadow-lg">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-[#FF4D00] text-white rounded-full flex items-center justify-center text-xl font-black">
                      {process.step}
                    </div>
                    <process.icon className="w-8 h-8 text-[#FF4D00]" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{process.title}</h3>
                  <p className="text-neutral-600 text-sm">{process.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testing Lab */}
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">In-House Testing Laboratory</h2>
                <p className="text-lg text-neutral-600 mb-6">
                  Our dedicated quality lab is equipped with advanced testing equipment to verify every aspect of product quality and safety. No product leaves our facility without passing rigorous testing.
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500" />
                    <span>Electrical safety testing equipment</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500" />
                    <span>Fast charging protocol analyzers</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500" />
                    <span>Durability testing machines</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500" />
                    <span>Temperature monitoring systems</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500" />
                    <span>Visual inspection stations</span>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-neutral-900 to-neutral-800 rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-6">Quality Commitment</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#FF4D00] rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold">100% Testing</h4>
                      <p className="text-neutral-400 text-sm">Every single product is tested, not just samples</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#FF4D00] rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold">Traceability</h4>
                      <p className="text-neutral-400 text-sm">Batch tracking from raw material to finished product</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#FF4D00] rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold">Continuous Improvement</h4>
                      <p className="text-neutral-400 text-sm">ISO 9001 requires ongoing quality improvements</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Warranty */}
        <section className="py-16 bg-neutral-900 text-white">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <Shield className="w-16 h-16 text-[#FF4D00] mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-4">1 Year Warranty on All Products</h2>
            <p className="text-neutral-300 mb-8 max-w-2xl mx-auto">
              Our confidence in quality is backed by a 1-year warranty. Manufacturing defects are covered with hassle-free replacement for distributors and their customers.
            </p>
            <Link to="/warranty-registration">
              <Button className="bg-[#FF4D00] hover:bg-[#E64500]">
                Register Warranty
              </Button>
            </Link>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#FF4D00]">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Partner with Quality-Focused Manufacturer</h2>
            <p className="text-white/90 mb-8">ISO certified, BIS compliant, 100% tested products</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-[#FF4D00] hover:bg-neutral-100">
                  <Phone className="w-5 h-5 mr-2" />
                  Call: +91-9311211889
                </Button>
              </Link>
              <Link to="/become-distributor">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#FF4D00]">
                  Become a Distributor
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default QualityCertifications;
